import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import {
  billingStatusTone,
  formatUnix,
  formatUnixDate,
  fromDatetimeLocalValue,
  selectClassName,
  toDatetimeLocalValue,
} from "./shared";

type BillingList = {
  summary: {
    active: number;
    pastDue: number;
    trialing: number;
    none: number;
    failedStripeReports: number;
  };
  accounts: Array<{
    accountId: string;
    organizationName: string;
    billingStatus?: string | null;
    trialEndsAt?: number | null;
    paidStartedAt?: number | null;
    stripeSubscriptionId?: string | null;
    currentPeriodStart?: number | null;
    currentPeriodEnd?: number | null;
    activeStreams: number;
    attendeesThisPeriod: number;
  }>;
  failedOutbox: Array<{
    eventId: string;
    accountId: string;
    attempts: number;
    lastError?: string | null;
    updatedAt: number;
  }>;
};

type BillingDetail = {
  account?: {
    id: string;
    name: string;
    accountType?: string;
  } | null;
  billing: {
    status: string;
    trialEndsAt?: number | null;
    trialStartedAt?: number | null;
    adminAccess?: string;
    adminAccessUntil?: number | null;
    adminAccessNote?: string | null;
    stripeSubscriptionId?: string | null;
    currentPeriodStart?: number | null;
    currentPeriodEnd?: number | null;
  };
  usage?: {
    activeStreams?: number;
    attendeesProcessed?: number;
    estimatedTotalCents?: number;
  };
  ledger?: Array<{
    id: string;
    eventType: string;
    amountCents?: number | null;
    occurredAt: number;
  }>;
  stripeLinkage?: {
    stripeSubscriptionId: string | null;
    currentPeriodStart: number | null;
    currentPeriodEnd: number | null;
  };
};

export function SuperAdminSubscriptionsPage() {
  const [data, setData] = useState<BillingList | null>(null);

  useEffect(() => {
    void api<BillingList>("/api/platform/billing")
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data) return <PageSkeleton />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Subscriptions</h1>
        <p className="text-sm text-rs-ink-muted">
          Account-level billing, trials, and Stripe linkage.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: "Active Paid", value: data.summary.active },
          { label: "Past Due", value: data.summary.pastDue },
          { label: "Trialing", value: data.summary.trialing },
          { label: "None", value: data.summary.none },
          {
            label: "Failed Stripe Reports",
            value: data.summary.failedStripeReports,
          },
        ].map((s) => (
          <Panel key={s.label} className="p-4">
            <p className="text-sm text-rs-ink-muted">{s.label}</p>
            <p className="mt-1 text-2xl font-semibold text-rs-ink">{s.value}</p>
          </Panel>
        ))}
      </div>
      <Panel>
        <PanelHeader
          title="Accounts"
          description="Open an account to extend trial, grant access, or adjust"
        />
        <ul className="divide-y divide-rs-border">
          {data.accounts.map((a) => (
            <li key={a.accountId}>
              <Link
                to={`/superadmin/subscriptions/${a.accountId}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm hover:bg-rs-muted"
              >
                <div>
                  <p className="font-medium text-rs-ink">
                    {a.organizationName}
                  </p>
                  <p className="text-rs-ink-muted">
                    {a.activeStreams} active stream
                    {a.activeStreams === 1 ? "" : "s"} ·{" "}
                    {Number(a.attendeesThisPeriod ?? 0).toLocaleString()}{" "}
                    attendees this period
                  </p>
                </div>
                <StatusBadge tone={billingStatusTone(a.billingStatus)}>
                  {a.billingStatus ?? "none"}
                </StatusBadge>
              </Link>
            </li>
          ))}
          {data.accounts.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
              No accounts yet.
            </li>
          ) : null}
        </ul>
      </Panel>
      {data.failedOutbox.length > 0 ? (
        <Panel>
          <PanelHeader title="Failed Stripe Meter Reports" />
          <ul className="divide-y divide-rs-border">
            {data.failedOutbox.map((row) => (
              <li key={row.eventId} className="px-5 py-3 text-sm">
                <p className="font-medium text-rs-ink">{row.eventId}</p>
                <p className="text-rs-ink-muted">
                  Account {row.accountId} · {row.attempts} attempt
                  {row.attempts === 1 ? "" : "s"}
                  {row.lastError ? ` · ${row.lastError}` : ""}
                </p>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

type FinancialAction =
  | { kind: "trial" }
  | { kind: "admin-access" }
  | { kind: "revoke-access" }
  | { kind: "adjustment" };

export function SuperAdminSubscriptionDetailPage() {
  const { accountId = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<BillingDetail | null>(null);
  const [orgName, setOrgName] = useState(accountId);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [action, setAction] = useState<FinancialAction | null>(null);
  const [busy, setBusy] = useState(false);

  const [trialEndsAt, setTrialEndsAt] = useState("");
  const [reopenTrialing, setReopenTrialing] = useState(true);
  const [trialReason, setTrialReason] = useState("");
  const [adminAccess, setAdminAccess] = useState<"comp" | "trial_extension">(
    "comp",
  );
  const [adminUntil, setAdminUntil] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [adjustCents, setAdjustCents] = useState("0");
  const [adjustNote, setAdjustNote] = useState("");

  async function refresh() {
    const detail = await api<BillingDetail>(
      `/api/platform/billing/accounts/${encodeURIComponent(accountId)}`,
    );
    setData(detail);
    if (detail.account?.name) setOrgName(detail.account.name);
    setTrialEndsAt(toDatetimeLocalValue(detail.billing.trialEndsAt ?? null));
  }

  useEffect(() => {
    setLoading(true);
    void refresh()
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  if (loading) return <PageSkeleton />;
  if (!data) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-rs-ink">
          Subscription Not Found
        </h1>
        <Button
          variant="secondary"
          onClick={() => navigate("/superadmin/subscriptions")}
        >
          Back to Subscriptions
        </Button>
      </div>
    );
  }

  const billing = data.billing;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-rs-ink-muted">
          <Link
            className="text-rs-accent hover:underline"
            to="/superadmin/subscriptions"
          >
            Subscriptions
          </Link>
          {" / "}
          {orgName}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold text-rs-ink">{orgName}</h1>
          <StatusBadge tone={billingStatusTone(billing.status)}>
            {billing.status}
          </StatusBadge>
          {billing.adminAccess && billing.adminAccess !== "none" ? (
            <StatusBadge tone="warning">Admin Access</StatusBadge>
          ) : null}
        </div>
        <p className="text-sm text-rs-ink-muted">
          <Link
            className="text-rs-accent hover:underline"
            to={`/superadmin/accounts/${accountId}`}
          >
            View Organization
          </Link>
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Trial Ends</p>
          <p className="mt-1 font-medium text-rs-ink">
            {formatUnixDate(billing.trialEndsAt)}
          </p>
        </Panel>
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Period</p>
          <p className="mt-1 text-sm font-medium text-rs-ink">
            {formatUnixDate(billing.currentPeriodStart)} –{" "}
            {formatUnixDate(billing.currentPeriodEnd)}
          </p>
        </Panel>
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Active Streams</p>
          <p className="mt-1 text-xl font-semibold text-rs-ink">
            {(data.usage?.activeStreams ?? 0).toLocaleString()}
          </p>
        </Panel>
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Estimate</p>
          <p className="mt-1 text-xl font-semibold text-rs-ink">
            {data.usage?.estimatedTotalCents != null
              ? `$${(data.usage.estimatedTotalCents / 100).toFixed(2)}`
              : "—"}
          </p>
        </Panel>
      </div>

      <Panel className="space-y-3 p-5">
        <h2 className="font-semibold text-rs-ink">Admin Actions</h2>
        <p className="text-sm text-rs-ink-muted">
          Financial and entitlement changes require confirmation and an audit
          note.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setAction({ kind: "trial" })}
          >
            Extend Trial
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setAction({ kind: "admin-access" })}
          >
            Grant Admin Access
          </Button>
          {billing.adminAccess && billing.adminAccess !== "none" ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAction({ kind: "revoke-access" })}
            >
              Revoke Admin Access
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={() => setAction({ kind: "adjustment" })}
          >
            Record Adjustment
          </Button>
        </div>
        {billing.adminAccessNote ? (
          <p className="text-sm text-rs-ink-muted">
            Access note: {billing.adminAccessNote}
          </p>
        ) : null}
        <p className="font-mono text-xs text-rs-ink-subtle">
          Stripe:{" "}
          {data.stripeLinkage?.stripeSubscriptionId ??
            billing.stripeSubscriptionId ??
            "—"}
        </p>
      </Panel>

      {data.ledger && data.ledger.length > 0 ? (
        <Panel>
          <PanelHeader title="Recent Ledger" />
          <ul className="divide-y divide-rs-border">
            {data.ledger.slice(0, 25).map((row) => (
              <li
                key={row.id}
                className="flex justify-between gap-2 px-5 py-3 text-sm"
              >
                <div>
                  <p className="font-medium text-rs-ink">{row.eventType}</p>
                  <p className="text-rs-ink-muted">
                    {formatUnix(row.occurredAt)}
                  </p>
                </div>
                <p className="font-medium text-rs-ink">
                  {row.amountCents != null
                    ? `$${(row.amountCents / 100).toFixed(2)}`
                    : "—"}
                </p>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {message ? <p className="text-sm text-rs-ink-muted">{message}</p> : null}

      <ConfirmDialog
        open={action?.kind === "trial"}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        title="Extend Trial"
        description="Update trial end date. Optionally reopen status to trialing."
        impact={
          <div className="space-y-2">
            <Input
              type="datetime-local"
              value={trialEndsAt}
              onChange={(e) => setTrialEndsAt(e.target.value)}
              aria-label="Trial Ends At"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={reopenTrialing}
                onChange={(e) => setReopenTrialing(e.target.checked)}
              />
              Reopen as Trialing
            </label>
            <Input
              value={trialReason}
              onChange={(e) => setTrialReason(e.target.value)}
              placeholder="Reason (required)"
              aria-label="Trial Reason"
            />
          </div>
        }
        confirmLabel="Extend Trial"
        dangerous
        busy={busy}
        onConfirm={async () => {
          const ends = fromDatetimeLocalValue(trialEndsAt);
          if (ends == null || !trialReason.trim()) {
            setMessage("Trial end and reason are required.");
            return;
          }
          setBusy(true);
          try {
            await api(
              `/api/platform/billing/accounts/${encodeURIComponent(accountId)}/trial`,
              {
                method: "POST",
                body: JSON.stringify({
                  trialEndsAt: ends,
                  reopenTrialing,
                  reason: trialReason.trim(),
                }),
              },
            );
            setAction(null);
            setTrialReason("");
            setMessage("Trial updated.");
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Trial update failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={action?.kind === "admin-access"}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        title="Grant Admin Access"
        description="Complimentary or trial-extension entitlement without creating a Stripe subscription."
        impact={
          <div className="space-y-2">
            <select
              className={selectClassName}
              value={adminAccess}
              onChange={(e) =>
                setAdminAccess(e.target.value as "comp" | "trial_extension")
              }
              aria-label="Admin Access Type"
            >
              <option value="comp">Complimentary</option>
              <option value="trial_extension">Trial Extension</option>
            </select>
            <Input
              type="datetime-local"
              value={adminUntil}
              onChange={(e) => setAdminUntil(e.target.value)}
              aria-label="Access Until"
            />
            <Input
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Note (required)"
              aria-label="Admin Access Note"
            />
          </div>
        }
        confirmLabel="Grant Access"
        dangerous
        busy={busy}
        onConfirm={async () => {
          if (!adminNote.trim()) {
            setMessage("Note is required.");
            return;
          }
          setBusy(true);
          try {
            await api(
              `/api/platform/billing/accounts/${encodeURIComponent(accountId)}/admin-access`,
              {
                method: "POST",
                body: JSON.stringify({
                  adminAccess,
                  adminAccessUntil: fromDatetimeLocalValue(adminUntil),
                  note: adminNote.trim(),
                }),
              },
            );
            setAction(null);
            setAdminNote("");
            setMessage("Admin access granted.");
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Admin access update failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={action?.kind === "revoke-access"}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        title="Revoke Admin Access"
        description="Remove complimentary or trial-extension entitlement for this account."
        impact={<p>adminAccess → none</p>}
        confirmLabel="Revoke Access"
        dangerous
        busy={busy}
        onConfirm={async () => {
          setBusy(true);
          try {
            await api(
              `/api/platform/billing/accounts/${encodeURIComponent(accountId)}/admin-access`,
              {
                method: "POST",
                body: JSON.stringify({ adminAccess: "none" }),
              },
            );
            setAction(null);
            setMessage("Admin access revoked.");
            await refresh();
          } catch (err) {
            setMessage(err instanceof Error ? err.message : "Revoke failed");
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={action?.kind === "adjustment"}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        title="Record Billing Adjustment"
        description="Ledger adjustment in cents (negative = credit). Non-zero amounts require a note."
        impact={
          <div className="space-y-2">
            <Input
              type="number"
              value={adjustCents}
              onChange={(e) => setAdjustCents(e.target.value)}
              placeholder="Amount cents"
              aria-label="Adjustment Amount Cents"
            />
            <Input
              value={adjustNote}
              onChange={(e) => setAdjustNote(e.target.value)}
              placeholder="Note (required if non-zero)"
              aria-label="Adjustment Note"
            />
          </div>
        }
        confirmLabel="Record Adjustment"
        dangerous
        busy={busy}
        onConfirm={async () => {
          const amountCents = Math.trunc(Number(adjustCents));
          if (!Number.isFinite(amountCents)) {
            setMessage("Amount must be a number.");
            return;
          }
          if (amountCents !== 0 && !adjustNote.trim()) {
            setMessage("Note is required for non-zero adjustments.");
            return;
          }
          setBusy(true);
          try {
            await api(
              `/api/platform/billing/accounts/${encodeURIComponent(accountId)}/adjustments`,
              {
                method: "POST",
                body: JSON.stringify({
                  amountCents,
                  note: adjustNote.trim() || undefined,
                }),
              },
            );
            setAction(null);
            setAdjustCents("0");
            setAdjustNote("");
            setMessage("Adjustment recorded.");
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Adjustment failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}
